import { BarcodeFormat } from "barcode-detector";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import AlertDefault, { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import BottomLine from "~/src/components/BottomLine";
import ButtonDefault from "~/src/components/Button/ButtonDefault";
import { useFormDataContext } from "~/src/contexts/FormDataContext/FormDataContext";
import { useStateful } from "~/src/hooks/useStateful";
import BarcodeScanner from "~/src/pages/Registrering/components/BarcodeScanner";
import { IndividFormDataContext } from "~/src/pages/Registrering/components/IndividForm";
import { AccordionDefault } from "../../../components/Accordion/AccordionDefault";
import Input, { InputHandle } from "../../../components/Form/Input/Input";
import { RadioButton } from "../../../components/Form/Radio";
import { ArtType } from "../../../helpers/types";

const MERKELAPP_BARCODE_FORMATS: BarcodeFormat[] = ["code_128"];

export default function MerkelappOgCwd(): JSX.Element {
  const { formData, formErrors, setFormData } = useFormDataContext(IndividFormDataContext);
  const { LevertHjerneprøve } = formData;
  const setMerkelapp = (merkelapp?: string) => setFormData({ ...formData, Merkelapp: merkelapp });
  const setLevertHjerneprøve = (levert: boolean) =>
    setFormData({ ...formData, LevertHjerneprøve: levert });

  const { t } = useTranslation();
  const scannerEnabled = useStateful(false);
  const merkelappInputRef = useRef<InputHandle | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const val = parseInt(target.value);
    const safe = isNaN(val) ? "" : val;
    setMerkelapp(`${safe}`);
  };

  const onScannerResult = (result: string) => {
    setMerkelapp(result);
    if (merkelappInputRef.current) {
      merkelappInputRef.current?.setValue(result);
    } else {
      console.error("Merkelapp input ref is not set or does not have a value");
    }
  };

  return (
    <AccordionDefault
      title={t("pages.registrerArt.merkelappOgCwd.title")}
      defaultOpen
      error={formErrors?.Merkelapp ? true : false}
    >
      <Input
        ref={merkelappInputRef}
        label={t("pages.registrerArt.merkelappOgCwd.merkelappNummerLabel")}
        id="input-radyr-merkelappNummer"
        type="text"
        inputMode="numeric"
        maxLength={12}
        value={formData.Merkelapp || ""}
        onChange={handleInputChange}
        errorMessage={formErrors?.Merkelapp}
      />
      <BarcodeScanner
        enabled={scannerEnabled}
        onResult={onScannerResult}
        barcodeFormats={MERKELAPP_BARCODE_FORMATS}
      />
      <ButtonDefault
        className={formErrors?.Merkelapp + " mt-4"}
        id="skann-merkelapp"
        label={
          scannerEnabled.value ? t("actions.close") : t("pages.registrerArt.merkelappOgCwd.skann")
        }
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="24"
            viewBox="0 0 30 24"
            fill="none"
            role="graphics-symbol"
          >
            <path
              d="M0 24V17.8286H2.05714V21.9429H6.17143V24H0ZM23.3143 24V21.9429H27.4286V17.8286H29.4857V24H23.3143ZM3.77143 20.2286V3.77143H6.51429V20.2286H3.77143ZM7.88571 20.2286V3.77143H9.25714V20.2286H7.88571ZM12 20.2286V3.77143H14.7429V20.2286H12ZM16.1143 20.2286V3.77143H20.2286V20.2286H16.1143ZM21.6 20.2286V3.77143H22.9714V20.2286H21.6ZM24.3429 20.2286V3.77143H25.7143V20.2286H24.3429ZM0 6.17143V0H6.17143V2.05714H2.05714V6.17143H0ZM27.4286 6.17143V2.05714H23.3143V0H29.4857V6.17143H27.4286Z"
              fill="currentColor"
            />
          </svg>
        }
        onClick={() => {
          scannerEnabled.set(!scannerEnabled.value);
          merkelappInputRef.current?.blur();
        }}
        />
      <BottomLine />
      <fieldset className="mt-6">
        <legend className="text-xl font-bold mb-1">
          {t("pages.registrerArt.merkelappOgCwd.hjerneprove")}
        </legend>
        <RadioButton
          name="group-radyr-levert-hjerneprove"
          label={t("actions.yes")}
          onChange={() => setLevertHjerneprøve(true)}
          checked={LevertHjerneprøve === true}
        />
        <RadioButton
          name="group-radyr-levert-hjerneprove"
          label={t("actions.no")}
          onChange={() => setLevertHjerneprøve(false)}
          checked={LevertHjerneprøve === false}
        />
        <BottomLine />
      </fieldset>
      {formData.LevertHjerneprøve && !formData.Merkelapp && (
        <AlertDefault variant={AlertVariant.Warning} className="mt-2">
          {t("pages.registrerArt.merkelappOgCwd.merkelappPåkrevd")}
        </AlertDefault>
      )}
    </AccordionDefault>
  );
}
